import { useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Button } from "@sortera/react-components";
import { ArrowUpIcon, LayoutGridIcon, PauseCircleIcon } from "@sortera/react-icons";
import styles from "./composer.module.css";

export interface ComposerProps {
  running: boolean;
  disabled?: boolean;
  placeholder: string;
  projectName: string;
  /** Controls beside the project chip, such as the model picker. */
  tools?: ReactNode;
  onSend: (text: string) => void;
  onStop: () => void;
}

const MAX_HEIGHT = 240;

/**
 * The message box. A bare, auto-growing textarea inside a card rather than the design system's
 * TextField: TextField is a bordered, fixed-height form field, and a chat box that grows with
 * what you write is a different control. That is a design-system gap — when the package gains a
 * borderless or auto-growing variant, this textarea should become it.
 */
export function Composer({ running, disabled, placeholder, projectName, tools, onSend, onStop }: ComposerProps) {
  const [text, setText] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, [text]);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || running || disabled) return;
    onSend(trimmed);
    setText("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter is a new line — and never mid-composition (dead keys, IME).
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      className={styles.composer}
      data-disabled={disabled}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      onClick={() => field.current?.focus()}
    >
      <textarea
        ref={field}
        className={styles.field}
        rows={2}
        aria-label="Meddelande"
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
      <div className={styles.bar}>
        <span className={styles.chip} title="Claude arbetar i det här projektet">
          <LayoutGridIcon size={14} />
          {projectName}
        </span>
        {tools}
        <span className={styles.hint}>Shift + Enter för ny rad</span>
        {running ? (
          <Button variant="outlined" color="secondary" size="small" icon={<PauseCircleIcon />} onClick={onStop}>
            Stoppa
          </Button>
        ) : (
          <Button type="submit" color="secondary" size="small" icon={<ArrowUpIcon />} disabled={disabled || !text.trim()}>
            Skicka
          </Button>
        )}
      </div>
    </form>
  );
}
